import { computed, inject, Injectable, isDevMode, signal } from '@angular/core';
import { BuilderCode, FiltreBuilder, LineOperator } from '../filtre-builders/filtre-builder.model';
import { FiltreBuilderService } from '../filtre-builders/filtre-builder.service';
import { FiltreLine } from './filtre-line.model';

@Injectable({
  providedIn: 'root',
})
export class FiltreLineService {
  private readonly builderService = inject(FiltreBuilderService);

  private readonly customBuilders: FiltreBuilder[] = [...this.builderService.getSelectableCustomBuilders()];

  private readonly buttonBuilders: FiltreBuilder[] = [...this.builderService.getButtonBuilders()];

  public allLines = signal<FiltreLine[]>([]);

  public buttonLines = computed(() => this.allLines().filter(l => l.isButton));
  public customLines = computed(() => this.allLines().filter(l => !l.isButton));

  constructor() {
    this.init();
  }
  init() {
    const buttonLines = this.buttonBuilders.map((b, index) => new FiltreLine(b.getThemeCode(), b.getBuilderCode(), b, index));
    this.allLines.set(buttonLines);
  }

  updateLines(lines: FiltreLine[]) {
    const customLinesToUpdate = lines.filter(l => !l.isButton);
    const buttonLinesToUpdate = lines.filter(l => l.isButton);

    // Mise à jour des boutons existants
    const initButtonLines = this.buttonBuilders.map((b, index) => new FiltreLine(b.getThemeCode(), b.getBuilderCode(), b, index));
    const updatedButtonLines = buttonLinesToUpdate.map(b => {
      const existingLine = initButtonLines.find(l => l.builderCode === b.builderCode);
      return existingLine ? existingLine.withValue(b.value) : b;
    });

    this.allLines.set([...updatedButtonLines, ...customLinesToUpdate]);
  }

  findBuilderByCode(itemCode: BuilderCode | null): FiltreBuilder | undefined {
    return [...this.customBuilders, ...this.buttonBuilders].find(f => f.getBuilderCode() === itemCode);
  }

  cleanAllLines() {
    const resetButtons = this.buttonLines().map(line => line.withValue(null));
    this.allLines.set(resetButtons);
  }

  removeCustomLine(order: number) {
    this.allLines.update(lines => lines.filter(line => !(line.order === order && !line.isButton)));
  }

  clearButtonLine(order: number) {
    this.allLines.update(lines => lines.map(line => (line.isButton && line.order === order ? line.withValue(null) : line)));
  }
  mapFiltreDataToLine(data: { builderCode: BuilderCode; operator: LineOperator; value: any }): FiltreLine | null {
    let resultLine: FiltreLine | null = null;
    try {
      const filtreBuilderCode = data.builderCode;
      const builder = this.builderService.findBuilderByItemCode(filtreBuilderCode);
      if (!builder) {
        throw new Error(`Builder not found for code: ${filtreBuilderCode}`);
      }
      const line = new FiltreLine(builder?.getThemeCode(), builder?.getBuilderCode(), builder);
      line.operator = data.operator;
      line.value = data.value;
      resultLine = line;
    } catch (error) {
      if (isDevMode()) {
        console.error('Error parsing filtre data to line:', error);
      }
      return null;
    }

    return resultLine;
  }

  setLinesFromFiltreSettings(filtres: { builderCode: BuilderCode; operator: LineOperator; value: any }[]) {
    const mapped = filtres.map(f => this.mapFiltreDataToLine(f)).filter((l): l is FiltreLine => !!l);

    this.allLines.update(() => {
      // On garde les lignes boutons existantes
      const buttons = this.buttonLines();
      const customs = mapped.filter(l => !l.isButton);

      // Mise à jour des boutons existants (valeurs clonées)
      const updatedButtons = buttons.map(b => {
        const matching = mapped.find(l => l.isButton && l.builderCode === b.builderCode);
        return matching ? b.withValue(matching.value) : b.withValue(null);
      });

      return [...updatedButtons, ...customs];
    });
  }
}
